import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';
import { Typography } from '@material-ui/core';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  ThunkGetIntegrationPriorities,
  ThunkGetIntegrationCompanies,
  ThunkGetZendeskIntegrationStatuses,
  ThunkGetZendeskIntegrationTypes,
  ThunkGetIntegrationServiceBoards,
} from '@store/slices/integration/thunks';
import crmSources from '@constants/crmSources';

import Input from '@common/Input/Input';
import DropDown from '@components/Auth/Common/DropDown';
import ReusableButton from '@common/Button/Button';
import AutocompleteFormInput from '@common/AutocompleteFormInput/AutocompleteFormInput';
import Loading from '@ui/components/common/Loading/Loading';

import crmDefaultFields from '../updateCrm/CrmDefaults/constants';
import {
  getInitFormData,
  validateFormData,
} from '../updateCrm/CrmDefaults/formData';
import useStyles from '../updateCrm/styles';
import { baseFieldName } from '../updateCrm/CrmDefaults/SubForms/helper';

const ZendeskDefaultForm = ({ onSubmit, onBack, crmId, data }) => {
  const [loading, setLoading] = useState(true);

  /// OnMount options
  const [priorities, setPriorities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [serviceBoards, setServiceBoards] = useState([]);
  // Board related options

  const classes = useStyles();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const submit = values => {
    const payload = {
      id: data ? data.id : 0,
      name: values.name,
      defaultBoardId: values.defaultBoardId,
      defaultBoard: serviceBoards.find(
        board => +board.id === +values.defaultBoardId,
      )?.name,
      defaultCompany: companies.find(
        cp => +cp.value === +values.crmSpecificSettings?.defaultCompany,
      )?.label,
      crmSpecificSettings: values.crmSpecificSettings,
    };

    onSubmit(payload);
  };

  const onMount = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      new Promise(res =>
        res(dispatch(ThunkGetIntegrationPriorities({ id: crmId })).unwrap()),
      ),
      new Promise(res =>
        res(dispatch(ThunkGetIntegrationCompanies({ id: crmId })).unwrap()),
      ),
      new Promise(res =>
        res(dispatch(ThunkGetZendeskIntegrationTypes({ crmId })).unwrap()),
      ),
      new Promise(res =>
        res(dispatch(ThunkGetZendeskIntegrationStatuses({ crmId })).unwrap()),
      ),
      new Promise(res =>
        res(dispatch(ThunkGetIntegrationServiceBoards({ id: crmId })).unwrap()),
      ),
    ])
      .then(res => {
        setPriorities(res[0]);
        setCompanies(res[1]);
        setTicketTypes(res[2]);
        setStatuses(res[3]);
        setServiceBoards(res[4]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, crmId, data]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loading />;
  return (
    <div className={classes.container}>
      <div className={classes.block}>
        <Form
          onSubmit={submit}
          initialValues={data ? getInitFormData(data) : {}}
          validate={values =>
            validateFormData(values, translate, crmSources.Zendesk.name)
          }
          render={({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <div className={classes.form}>
                <div>
                  <Typography className={classes.inputLabel}>Name:</Typography>
                  <Field
                    id="name"
                    name="name"
                    styleType="main"
                    inputView="text"
                    component={Input}
                    placeholder="Name"
                    fullWidth
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    Default Board:
                  </Typography>
                  <Field
                    id={crmDefaultFields.defaultBoardId}
                    name={crmDefaultFields.defaultBoardId}
                    size="small"
                    labelName="name"
                    valueName="id"
                    component={DropDown}
                    options={serviceBoards}
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    Catch-all Company:
                  </Typography>
                  <Field
                    id={baseFieldName + crmDefaultFields.defaultCompany}
                    name={baseFieldName + crmDefaultFields.defaultCompany}
                    size="small"
                    component={AutocompleteFormInput}
                    items={companies}
                    suppressInlineLabel
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    Ticket Type:
                  </Typography>
                  <Field
                    id={baseFieldName + crmDefaultFields.typeId}
                    name={baseFieldName + crmDefaultFields.typeId}
                    size="small"
                    component={DropDown}
                    labelName="name"
                    valueName="value"
                    allowEmpty
                    options={ticketTypes}
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    New Ticket Status:
                  </Typography>
                  <Field
                    id={baseFieldName + crmDefaultFields.openBoardStatusId}
                    name={baseFieldName + crmDefaultFields.openBoardStatusId}
                    size="small"
                    labelName="name"
                    valueName="value"
                    component={DropDown}
                    options={statuses}
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    Closed Ticket Status:
                  </Typography>
                  <Field
                    id={baseFieldName + crmDefaultFields.closedBoardStatusId}
                    name={baseFieldName + crmDefaultFields.closedBoardStatusId}
                    size="small"
                    labelName="name"
                    valueName="value"
                    component={DropDown}
                    options={statuses}
                  />
                </div>

                <div>
                  <Typography className={classes.inputLabel}>
                    Ticket Priority:
                  </Typography>
                  <Field
                    id={baseFieldName + crmDefaultFields.ticketPriority}
                    name={baseFieldName + crmDefaultFields.ticketPriority}
                    size="small"
                    labelName="name"
                    valueName="name"
                    component={DropDown}
                    options={priorities}
                  />
                </div>
              </div>
              <div className={classes.buttonsWrapper}>
                <ReusableButton
                  size="md"
                  type="button"
                  label="Cancel"
                  classNameWrapper={cx(
                    classes.buttonCancel,
                    classes.buttonStyle,
                  )}
                  onClick={onBack}
                />
                <ReusableButton
                  size="md"
                  classNameWrapper={classes.buttonStyle}
                  viewType="black"
                  type="submit"
                  onClick={() => submit(values)}
                  label="resources.buttons.submit"
                />
              </div>
            </form>
          )}
        />
      </div>
    </div>
  );
};

ZendeskDefaultForm.propTypes = {
  onSubmit: PropTypes.func,
  onBack: PropTypes.func,
  crmId: PropTypes.number,
  data: PropTypes.shape({
    defaultBoardId: PropTypes.number,
    id: PropTypes.string,
  }),
};

export default ZendeskDefaultForm;
